import { pipe, rawTransform } from 'valibot';
import { MixedDlsTeaserResponse, ProductDto } from '../../types/dls/dls-response/ProductDto';
import { MixedDlsSearchAlternativeReponseWithId } from '../../types/dls/dls-response/SearchResponseDto';
import { appendUserContextToUrl } from '../../util/EnhanceUrlWithCtx';
import { handleSrpUrl } from '../../util/HandleUrls';
import { SchemaAny } from '../VehicleTileDataSchema';
import { TCampaignTileAemPayload } from '../campaign-tile/CampaignTileData';
import { generatePrices } from '../helper/prices';
import { SimplePriceVehicleDataFormat } from '../simple-price/SimplePriceTransformer';

export const ConvertDlsSearchAlternativeToSimplePriceVehicleDataTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsSearchAlternativeReponseWithId, SimplePriceVehicleDataFormat>(
    ({ dataset, addIssue: _addIssue, NEVER }) => {
      const url = handleSrpUrl(
        dataset.value.url,
        dataset.value.rawData?.ctaUrl,
        {
          sortByLeasingrate: dataset.value.rawData?.sortByLeasingrate,
        },
        dataset.value.ctx,
      );
      const vehicleData = getVehicleData(dataset.value);
      if (!dataset.value.rawData) return NEVER;
      const rawData: TCampaignTileAemPayload = dataset.value.rawData;
      if (!vehicleData.dcpProductType?.name) return NEVER;
      return {
        heading: rawData.tileHeadline != null ? rawData.tileHeadline : '',
        description: rawData?.tileDescription !== null ? rawData?.tileDescription : '',
        image:
          rawData?.croppedimage !== null
            ? {
                url: rawData?.croppedimage?.sources?.mq1,
                alt: rawData?.croppedimage?.description !== null ? rawData?.croppedimage?.description : '',
                title: rawData?.croppedimage?.title !== null ? rawData?.croppedimage?.title : '',
              }
            : undefined,

        cta: {
          label: rawData?.ctaLabel,
          srp: url,
        },
        tag: {
          badges: [
            {
              showIcon: false,
              badges: rawData?.recommendationBadge
                ? {
                    name: rawData?.recommendationBadge?.name,
                    description:
                      rawData?.recommendationBadge?.description !== null
                        ? rawData?.recommendationBadge?.description
                        : '',
                    footnote:
                      rawData?.recommendationBadge?.footnote !== null ? rawData?.recommendationBadge?.footnote : '',
                  }
                : {},
            },
          ],
        },
        prices: {
          prices: generatePrices({
            productCode: vehicleData.code,
            commissionNumber: vehicleData.commissionNumber,
            configurationId: vehicleData.configurationId,
            dcpProductType: vehicleData.dcpProductType?.name,
            productType: 'STOCK',
            market: dataset.value.market,
            locale: dataset.value.lang,
            currency: vehicleData.price?.currencyIso,
            priceRawValue: vehicleData.price?.value,
          }),
        },
        common: {
          common: {
            hasCriticalError: false,
            brand: vehicleData.brand,
            productType: 'STOCK',
            bm6: vehicleData.baumuster,
            modelIdentifier: vehicleData.modelIdentifier?.code,
            bodyType: vehicleData.bodyType?.code,
            vehicleName: vehicleData.name,
            identification: {
              commissionNumber: vehicleData.commissionNumber,
              productCode: vehicleData.code ?? 'no code',
              configurationId: undefined,
            },
            isVan: false,
          },
        },
        mixedDlsTeaserresponse: {
          ...vehicleData,
          mode: dataset.value.mode,
          translations: dataset.value.translations,
          market: dataset.value.market,
          lang: dataset.value.lang,
          responseTypeId: 'ProductDto',
        },
      };
    },
  ),
);

function getVehicleData(vehicle: MixedDlsSearchAlternativeReponseWithId): Partial<ProductDto> {
  if (vehicle.firstVehicles && vehicle.firstVehicles?.length > 0) {
    return {
      ...vehicle.firstVehicles[0],
    };
  } else if (vehicle.firstVehicle) {
    return {
      ...vehicle.firstVehicle,
    };
  }

  throw Error('No Vehicle was provided');
}

export const ConvertDlsSearchAlternativeToDlsTeaserTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsSearchAlternativeReponseWithId, MixedDlsTeaserResponse>(({ dataset, addIssue, NEVER }) => {
    if (dataset.value.firstVehicles && dataset.value.firstVehicles.length > 0) {
      return {
        ...dataset.value.firstVehicles[0],
        srpUrl: appendUserContextToUrl(dataset.value.url, dataset.value.ctx),
        responseTypeId: 'ProductDto',
        translations: dataset.value.translations,
        market: dataset.value.market,
        lang: dataset.value.lang,
        mode: dataset.value.mode,
        brand: dataset.value.brand,
        sortType: dataset.value.sortType,
      };
    } else if (dataset.value.firstVehicle) {
      return {
        ...dataset.value.firstVehicle,
        srpUrl: appendUserContextToUrl(dataset.value.url, dataset.value.ctx),
        responseTypeId: 'ProductDto',
        translations: dataset.value.translations,
        market: dataset.value.market,
        mode: dataset.value.mode,
        lang: dataset.value.lang,
        brand: dataset.value.brand,
        sortType: dataset.value.sortType,
      };
    } else {
      addIssue({
        message: 'No vehicle data available in the response',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
  }),
);
